<template>
  <main>
    <div class="header md-elevation-1">
      <gl-logo-app onDarkBackground class="logo" />
      <div class="align-left">
        <md-tabs
          :md-active-tab="activeTab"
          @md-changed="$emit('update:activeTab', $event)"
        >
          <md-tab
            id="filter"
            md-label="分析条件"
            md-icon="filter_list"
          ></md-tab>
          <md-tab
            id="result"
            md-label="分析結果"
            md-icon="table_chart"
            :md-disabled="!queryId"
          ></md-tab>
        </md-tabs>
      </div>
      <div class="header-actions">
        <transition name="slide-in">
          <div
            v-if="activeTab === 'result' && !querying"
            class="header-actions"
          >
            <div
              v-if="!hideRestrictedMode && !$store.state.isQueryFullFeatured"
              class="gl-tooltip-wide icon-with-text"
              :gl-tooltip="msg.MSG_QUERY_FULL_FEATURE_UNAVAILABLE_DETAIL"
              gl-tooltip-direction="down"
            >
              <md-icon>lock</md-icon>
              <span>制限モード中</span>
            </div>
            <slot name="header-actions" :query-id="queryId" />
            <div
              :gl-tooltip="
                activeTab === 'result' && !querying ? 'Excel出力' : ''
              "
              gl-tooltip-direction="down"
            >
              <gl-button
                v-if="!noExcelDownload"
                :disabled="
                  activeTab !== 'result' || querying || downloadingExcel
                "
                @click="downloadExcel"
                icon-button
              >
                <md-icon :class="{ spin: downloadingExcel }">
                  <font-awesome-icon icon="file-excel" />
                </md-icon>
              </gl-button>
            </div>
          </div>
        </transition>
        <div class="header-actions">
          <div gl-tooltip="ヘルプ" gl-tooltip-direction="down">
            <gl-button icon-button @click="openHelp">
              <gl-icon-help />
            </gl-button>
          </div>
          <div :gl-tooltip="menu.menuLongName" gl-tooltip-direction="down">
            <md-avatar class="avatar">
              <img :src="menu.imageUrl" />
            </md-avatar>
          </div>
        </div>
      </div>
    </div>

    <!-- 条件項目一覧 -->
    <div class="filter-summary-wrapper">
      <div class="filter-summary">
        <md-list class="filter-summary-body md-double-line">
          <slot name="filter-summary">
            <component
              v-for="p of filterSummaryProperties"
              :key="p.name"
              :is="`filter-summary-${p.name}`"
              :scroll-to-id="p.name"
              v-bind="p.filter"
            />
          </slot>
        </md-list>
      </div>
    </div>

    <!-- コンテンツ -->
    <div class="contents">
      <div v-if="activeTab === 'filter'" id="filter" class="filter">
        <query-filter-element
          v-if="!disableReservation && $store.getters.isDeveloper"
          id="mode"
          title="実行モード"
          contentWidth="medium"
        >
          <reservation-setting
            v-bind.sync="reservationConfig"
            :available-cycle-types="availableCycleTypesForReservation"
          />
        </query-filter-element>
        <slot name="filter"></slot>
        <div v-if="querying" class="filter-querying-overlay">
          <div class="spinner-container">
            <img src="@/assets/images/Web_hosting.svg" />
            <spinner />
          </div>
        </div>
      </div>
      <div
        v-if="activeTab === 'result'"
        class="result-container"
        ref="resultContainer"
      >
        <slot v-if="querying" name="querying-spinner">
          <div class="spinner-container">
            <img src="@/assets/images/Web_hosting.svg" />
            <spinner />
          </div>
        </slot>
        <slot v-else name="result"></slot>
      </div>

      <transition name="query-button">
        <div
          v-if="activeTab === 'filter'"
          class="fixed-action-area"
          :gl-tooltip="buttonToolTip"
          gl-tooltip-direction="left"
        >
          <button-execute-query
            v-if="disableReservation || reservationConfig.mode !== 'reserve'"
            @click="executeQueryFilterSaveWrapper"
            class="animated"
            :disabled="!(canStartQuery && canTabSwitch)"
            :will-plane-fly="canStartQuery"
            :class="{ tada: canStartQuery }"
            :enable-dump-upload="$store.getters.isAdmin"
            @dump-uploaded="handleDumpUploaded"
          />
          <button-register-query
            v-else
            @click="reserveQuery"
            class="animated"
            :disabled="!(canStartQuery && canTabSwitch)"
            :is-edit="reservationConfig.isEdit"
            :will-register="canStartQuery"
            :class="{ tada: canStartQuery }"
            :reservation="reservationId"
          />
        </div>
      </transition>
    </div>

    <!-- 顧客コレクション -->
    <customer-collection
      :customers="customers"
      @update:customers="updateCustomers"
      @download="downloadCustomers"
      :downloading="downloadingCustomerFile"
    >
      <query-transitions
        :query-id="queryId"
        :immediate.sync="immediate"
        :disabled="customerDownloadOnly"
        :creating-customer-group="creatingCustomerGroup"
        @transition="transition"
      />
    </customer-collection>

    <!-- 顧客ファイルアップロード中通知 -->
    <md-dialog
      :md-active.sync="uploadingCustomerFile"
      :md-close-on-esc="false"
      :md-click-outside-to-close="false"
    >
      <md-dialog-content>
        <div style="display:flex;align-items:center">
          <spinner :diameter="30" />
          <span style="margin-left:var(--spacing-medium)">
            顧客一覧ファイルをアップロードしています...
          </span>
        </div>
      </md-dialog-content>
    </md-dialog>

    <md-dialog-alert
      :md-active.sync="hasCustomerFileUploadWarning"
      :md-content="customerFileUploadWarningMessage"
      md-confirm-text="Close"
    />

    <!-- 分析結果0件警告 -->
    <md-dialog-alert
      :md-active.sync="showNoResultDialog"
      md-content="対象となるデータがありませんでした"
      md-confirm-text="Close"
    />

    <!-- 分析条件復元失敗エラーダイアログ -->
    <md-dialog-alert
      :md-active.sync="showFailedToApplyQueryFilter"
      md-content="分析条件の読込に失敗しました。保存されていた分析条件は削除されます。"
      md-confirm-text="Close"
    />

    <md-snackbar
      md-position="center"
      :md-duration="snackbar.duration"
      :md-active.sync="snackbar.show"
      md-persistent
    >
      {{ snackbar.message }}
    </md-snackbar>
  </main>
</template>

<script>
import { kebabCase, mergeWith, isArray, differenceBy } from 'lodash'
import CustomerCollection from '@/components/CustomerCollection.vue'
import QueryTransitions from '@/components/QueryTransitions.vue'
import ButtonExecuteQuery from '@/components/ButtonExecuteQuery'
import ButtonRegisterQuery from '@/components/ButtonRegisterQuery'
import Spinner from '@/components/Spinner.vue'
import DialogWarning from '@/components/DialogWarning.vue'
import QueryFilterElement from '@/components/QueryFilterElement.vue'
import ReservationSetting from '@/components/ReservationSetting.vue'

import { initializeRestrictedQueryFilter } from '@/queryfilter/util'
import {
  SET_IS_QUERY_FULL_FEATURED,
  SET_QUERY_FILTER_RESTORING,
} from '@/const/mutation-types'
import msg from '@/const/messages'

import {
  INITIAL_RESERVE_CONFIG,
  canEditReservation,
  canResubmitReservation,
} from '@/const/statusConfig'
/**
 * 分析条件サマリコンポーネントを非同期コンポーネントのローカル登録形式にして取得する
 *
 * ```
 * components
 * └ FilterSummaryItem.vue
 * └ FilterSummaryOption.vue
 * └ FilterSummaryStore.vue
 * ```
 * ↓
 * ```
 * {
 *   FilterSummaryItem: () => import('../FilterSummaryItem.vue'),
 *   FilterSummaryOption: () => import('../FilterSummaryOption.vue'),
 *   FilterSummaryStore: () => import('../FilterSummaryStore.vue'),
 * }
 * ```
 */
function loadSummaryComponents() {
  const requireComponent = require.context(
    '../',
    false,
    /FilterSummary[\w-]+\.vue$/,
  )
  return requireComponent
    .keys()
    .map(fileName => {
      const name = fileName.replace(/^\.\//, '').replace(/\.\w+$/, '')

      return {
        name,
        import: () => import(`../${name}.vue`),
      }
    })
    .reduce((o, c) => ({ ...o, [c.name]: c.import }), {})
}

// function getMenuNameFrom(menuCode, store) {
//   const menu = store.state.menus.find(m => m.menuCode === menuCode)
//   if (!menu) {
//     return undefined
//   }
//   return menu.menuLongName
// }

/**
 * 分析画面基本レイアウト
 *
 * 分析条件・分析結果から成る画面の基本レイアウトです。分析条件を配置するスロットと
 * 分析結果を配置するスロットを提供します。
 *
 * イベント一覧
 *
 * * update:activeTab (tabId: String)
 *   * 開いているタブを変更したとき
 * * query-id-change (queryId: String)
 *   * 画面表示している分析IDが変化したとき
 * * update:resultFetchUrl (url: String)
 *   * 結果FetchURLが変化したとき
 * * update:columns (columns: Array<ColModel>)
 *   * 列定義が変化したとき
 * * update:groupHeader (groupHeaders: Array<Array<GroupHeader>>)
 *   * 列ヘッダ定義が変化したとき
 * * result-area-size-change ({ width: Number, height: Number })
 *   * 結果表示エリアのサイズが変化したとき
 * * query-completed (QueryInfo)
 *   * クエリが正常終了したとき TODO 異常終了した場合のイベントが必要になったら別途作る
 * * update:customers (customers: Array<Customer>)
 *   * 顧客コレクションの内容が変化したとき
 */
export default {
  components: {
    CustomerCollection,
    QueryTransitions,
    Spinner,
    ButtonExecuteQuery,
    ButtonRegisterQuery,
    QueryFilterElement,
    ReservationSetting,
    ...loadSummaryComponents(),
  },
  props: {
    /**
     * クエリリソース名
     *
     * この名前を基準にクエリAPIのエンドポイントの構築等を行います。
     */
    resourceName: {
      type: String,
      required: true,
    },
    /**
     * 分析条件
     *
     * クエリを発行する際のペイロードの元データです。
     * 分析条件サマリを作成、および分析リクエストを投げるために必要です。
     */
    queryFilter: {
      type: Object,
      required: true,
    },
    /**
     * 分析条件をクエリのペイロードに変換する関数
     *
     * これを使ってクエリを発行する際のペイロードに変換します。
     * 分析条件のオブジェクトは画面に依ってバラバラなので、変換処理も画面が定義して
     * 渡してくれる必要があります。
     */
    mapQueryFilterForApi: {
      type: Function,
      required: true,
    },
    /**
     * クエリ完了時のカスタムフック
     *
     * 分析がエラーとなった場合や結果が0件だった場合はデフォルトの対処が行われますが、
     * 分析特有の対応が必要な場合はこの関数を実装してください。
     *
     * この関数には`{queryFilter: ..., queryInfo: ...}`が渡されます。
     *
     * この関数は`{ stop: (boolean) }`を返してください。
     * stop=trueの場合、分析結果画面には遷移しません。
     */
    onQueryCompleted: {
      type: Function,
      required: false,
    },
    /**
     * クエリフィルタオブジェクトを自身のクエリフィルタへ適用する関数
     */
    applyQueryFilter: {
      type: Function,
      required: true,
    },
    /**
     * メニュー毎のデフォルト分析条件保存時の変換処理
     *
     * 一時商品グループIDのような、一定期間後に失効するデータなどを
     * 保存データから除外するための変換処理です。実際のところ、
     * POS分析でまず導入した商品ファイル指定のためです… ID-POSの
     * 顧客グループIDもこれを使えばいい。
     *
     * 指定がなければ何もしません。
     */
    convertMenuDefaultQueryFilter: {
      type: Function,
      required: false,
    },
    /**
     * 予約機能を無効にするフラグ
     */
    disableReservation: {
      type: Boolean,
      default: false,
    },
    /**
     * 分析結果をFetchするためのURL
     *
     * このコンポーネントではなく、分析結果テーブルを持つ側が必要なデータですが、
     * $longQueryから取得するものなのでこちらに寄せました。
     *
     * 実態は、親コンポーネントとの同期のためだけに存在します。
     */
    resultFetchUrl: {
      type: String,
    },
    noExcelDownload: {
      type: Boolean,
      default: false,
    },
    /**
     * 分析結果の列
     */
    columns: {
      type: Array,
      default: () => [],
    },
    /**
     * 分析結果のグループヘッダ
     */
    groupHeader: {
      type: Array,
      default: () => [],
    },
    /**
     * 選択されているタブ（分析条件:filter or 分析結果:result）
     */
    activeTab: {
      type: String,
      default: 'filter',
      validator(v) {
        return ['filter', 'result'].includes(v)
      },
    },
    /**
     * 選択顧客
     */
    customers: {
      type: Array,
      default: () => [],
    },
    /**
     * 顧客選択時の操作が顧客ダウンロードのみ
     *
     * trueの場合、顧客ダウンロードのみであり、画面遷移はできない。
     */
    customerDownloadOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * Excel出力処理で指定するクエリパラメータを返す関数
     *
     * 分析画面側の事情でExcel出力をキャンセルする場合、引き渡される関数では`false`
     * を返してください。このレイアウトではエラーメッセージ等の対応はしないので、
     * Excel出力がされない旨のユーザーへの通知は各分析側で行ってください。
     *
     * 例）
     * {
     *   sortColName: "store_code",
     *   sortOrder: "asc"
     * }
     *
     * => sortColumnName=code&sortOrder=asc
     */
    excelPropertiesHandler: {
      type: Function,
      default: () => ({}),
    },
    /**
     * ヘッダー部分の「🔒制限モード」の表示を設定
     */
    hideRestrictedMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      /**
       * 分析ID
       *
       * 分析を行う度にこの分析IDが更新されます。
       */
      queryId: undefined,
      /**
       * 分析予約ID
       */
      reservationId: undefined,
      /**
       * 元の分析予約ID
       * ※ステータスがNextReservationFailedの場合に、元となった予約をCompletedに更新するために保持しておく
       */
      parentReservationId: undefined,
      /**
       * 元の分析予約のステータスがNextReservationFailedかどうか
       * ※ステータスがNextReservationFailedの場合に、元となった予約をCompletedに更新するために保持しておく
       */
      isNextReservationFailed: undefined,
      /**
       * 予約条件
       */
      reservationConfig: { ...INITIAL_RESERVE_CONFIG },
      /**
       * クエリ実行中か否か
       */
      querying: false,
      /**
       * 顧客一覧ファイルのアップロード中フラグ
       */
      uploadingCustomerFile: false,
      /**
       * 顧客一覧ファイルアップロード時の警告有無
       */
      hasCustomerFileUploadWarning: false,
      /**
       * 顧客一覧ファイルアップロード時の警告メッセージ
       *
       * ファイルフォーマット不正や結果0件
       */
      customerFileUploadWarningMessage: undefined,
      /**
       * 顧客一覧ファイルダウンロード中フラグ
       *
       * ダウンロードURLが作成されるまでには少々時間がかかるので、ボタン制御等
       * をするためにフラグを用意します。
       */
      downloadingCustomerFile: false,
      /**
       * Excelファイルダウンロード中フラグ
       */
      downloadingExcel: false,
      /**
       * 画面遷移のための一時顧客グループ作成中フラグ
       */
      creatingCustomerGroup: false,
      /**
       * 分析結果0件ダイアログの開閉状態
       */
      showNoResultDialog: false,
      /**
       * 分析条件の復元失敗エラーダイアログの開閉状態
       */
      showFailedToApplyQueryFilter: false,

      /**
       * 遷移時に即時分析するか
       */
      immediate: true,
      /**
       * 分析開始後、分析結果タブに移るまでの間
       */
      waitingTabSwitch: false,
      /**
       * メッセージ定数
       */
      msg,
      snackbar: {
        show: false,
        duration: 2000,
        message: '',
      },
    }
  },
  computed: {
    /**
     * 分析条件サマリ用プロパティリスト
     *
     * 分析画面の分析条件から構築されます。
     *
     * 各分析条件（"期間"や"店舗"の粒度）のフォーマットは以下の通り。
     *
     * ```
     * {
     *   order: <Number | required> 指定がない場合サマリは作成されません
     *   summaryKey: <String | Optional>
     * }
     * ```
     *
     * 分析条件のトップレベルキーからサマリコンポーネントを特定します（
     * `filter-summary-${キーのケバブケース}`）。例えば以下の場合、
     *
     * ```
     * {
     *   term: {
     *     // 略
     *   },
     *   // 略
     * }
     * ```
     *
     * FilterSummaryTerm（filter-summary-term）コンポーネントが
     * 分析条件サマリとして使用されます。
     *
     * 使用するサマリコンポーネント名は、`summaryKey`を含めることで
     * 上書きすることができます。例えば以下の場合、
     *
     * ```
     * {
     *   term: {
     *     // 略
     *     summaryKey: 'termCompare',
     *   },
     *   // 略
     * }
     * ```
     *
     * FilterSummaryTermCompare（filter-summary-term-compare）
     * コンポーネントが分析条件サマリとして使用されます。
     *
     * また、orderが指定されていない条件はサマリを作成しません。
     */
    filterSummaryProperties() {
      return Object.entries(this.queryFilter)
        .filter(entry => entry[1].order !== undefined)
        .map(([name, filter]) => ({
          name: filter.summaryKey
            ? kebabCase(filter.summaryKey)
            : kebabCase(name),
          filter,
        }))
        .sort((p1, p2) => p1.filter.order - p2.filter.order)
    },
    /**
     * クエリの発行ができる状態か否か
     */
    canStartQuery() {
      return !this.hasInvalidInput
    },
    canTabSwitch() {
      return !this.waitingTabSwitch
    },
    /**
     * 分析ボタンツールチップ
     */
    buttonToolTip() {
      if (this.hasInvalidQueryFilterInput) {
        return '分析条件に入力エラーがあります。'
      }
      if (this.hasInvalidReservationInput) {
        return '予約条件に入力エラーがあります。'
      }
      return ''
    },
    /**
     * 分析条件に不正があるか
     *
     * 各分析条件から`errors`を読み取りエラーテキストが入っているか確認します。
     *
     * ```
     * queryFilter {
     *   term: {
     *     // 略
     *     errors: [] // ★ここを見る
     *   },
     *   store: {
     *     // 略
     *     errors: [ "店舗を選択してください" ] // ★ここを見る
     *   }
     * }
     * ```
     */
    hasInvalidQueryFilterInput() {
      return (
        Object.entries(this.queryFilter)
          .map(e => e[1])
          .map(e => (e['errors'] ? e.errors : []))
          .filter(e => e.length > 0).length > 0
      )
    },
    /**
     * 予約条件に不正があるか
     *
     * 予約条件(reservationConfig)の`errors`を読み取りエラーテキストが入っているか確認します。
     *
     * ```
     * reservationConfig {
     *   // 略
     *   mode: "reserve",
     *   errors: [] // ★ここを見る
     * }
     * ```
     */
    hasInvalidReservationInput() {
      if (
        this.reservationConfig.mode !== 'reserve' ||
        this.disableReservation
      ) {
        return false
      }
      return (
        this.reservationConfig.errors &&
        this.reservationConfig.errors.length > 0
      )
    },
    /**
     * 分析条件または予約条件に不正があるか
     */
    hasInvalidInput() {
      return this.hasInvalidQueryFilterInput || this.hasInvalidReservationInput
    },
    menu() {
      const menu = this.$store.state.menus.find(
        m => m.menuCode === this.resourceName,
      )
      if (!menu) {
        return {
          menuCode: undefined,
          menuLongName: undefined,
          imageUrl: undefined,
        }
      }
      return menu
    },
    /**
     * 予約設定用の利用可能な実行周期リスト
     * queryFilter の termType に基づいて計算される
     * ONCE: 今回のみ
     * WEEKLY: 毎週
     * MONTHLY: 毎月（補正有りで、定期実行の次回期間算出時、月初/月末の日付の補正する。）
     * MONTHLY_EXACT: 毎月（補正なしなので、単純に月を1加算する。）
     *
     * 注：queryFilter.term の構造は以下のパターンに対応
     * 1. 単純型: queryFilter.term.termType (多くの Query*.vue)
     * 2. 複合型: queryFilter.term.base と queryFilter.term.compare
     *    (QueryShrimp.vue など)
     *    複合型の場合は、base と compare の両方で共通する周期タイプのみを返す
     */
    availableCycleTypesForReservation() {
      // termType に対応する CycleType のマッピング
      const getCycleTypesForTermType = termType => {
        switch (termType) {
          case 'm': // 月
            return ['ONCE', 'MONTHLY']
          case 'w': // 週
            return ['ONCE', 'WEEKLY']
          case 'd': // 日
            return ['ONCE', 'WEEKLY', 'MONTHLY_EXACT']
          case 'select': // 選択日付
            return ['ONCE', 'WEEKLY', 'MONTHLY_EXACT']
          case 'closing': // 月度指定
            return ['ONCE', 'MONTHLY_EXACT']
          default:
            return ['ONCE', 'WEEKLY', 'MONTHLY_EXACT']
        }
      }

      if (!this.queryFilter || !this.queryFilter.term) {
        return ['ONCE', 'WEEKLY', 'MONTHLY_EXACT']
      }

      // 単純型の場合（queryFilter.term.termType）
      if (this.queryFilter.term.termType) {
        return getCycleTypesForTermType(this.queryFilter.term.termType)
      }

      // 複合型の場合（base.termType と compare.termType）
      // 両方に共通する周期タイプのみを返す
      if (this.queryFilter.term.base && this.queryFilter.term.compare) {
        const baseCycleTypes = getCycleTypesForTermType(
          this.queryFilter.term.base.termType,
        )
        const compareCycleTypes = getCycleTypesForTermType(
          this.queryFilter.term.compare.termType,
        )
        return baseCycleTypes.filter(cycleType =>
          compareCycleTypes.includes(cycleType),
        )
      }

      // 単純に base のみが存在する場合
      if (this.queryFilter.term.base) {
        return getCycleTypesForTermType(this.queryFilter.term.base.termType)
      }

      return ['ONCE', 'WEEKLY', 'MONTHLY_EXACT']
    },
  },
  watch: {
    queryId(queryId) {
      this.$emit('query-id-change', queryId)
    },
    activeTab() {
      // 分析条件、分析結果切替時には顧客コレクションクリア
      this.$emit('update:customers', [])
    },
    //
    // queryIdの変化で実施することは以下の3つ
    //
    // 1. 分析IDが提供されている
    //
    //    この画面で結果を表示したい分析IDです。分析ステータスは分析中、若しくは完了
    //    （異常終了含む）両方を考慮に入れる必要があります。
    //
    // 2. 参照分析IDが提供されている
    //
    //    画面遷移により開かれた画面です。参照分析IDで指定された分析IDの分析条件
    //    を使い（一部変更）このResourceで分析します。分析が完了したら分析IDをURLに
    //    セットし、1に飛ばします（結果の表示は1のルートに任せます）。
    //
    // 3. 分析IDも参照分析IDもない
    //
    //    メニューから開いた初期画面です。分析後の画面から戻ってきた場合もこのケースです。
    //    分析条件等を初期状態に戻すのを忘れずに。
    //
    '$route.params.queryId': {
      // 画面表示時にもキャッチできるようにimmediateとしています
      immediate: true,
      async handler(queryId) {
        const referenceId = this.$route.query.reference
        const reservationId = this.$route.query.reservation
        if (queryId) {
          // 1. 分析IDが提供されている
          this.queryId = queryId

          this.querying = true

          const queryFilter = await this.$longQuery.fetchQueryFilter(queryId)
          if (!queryFilter) {
            throw new Error('分析IDが見つかりませんでした。', queryId)
          }

          const promises = this.applyQueryFilter(queryFilter)
          if (promises && promises.length > 0) {
            await Promise.all(promises.filter(p => p))
          }

          // QueryFilterへの反映が完了し、各条件コンポーネントのエラー判定が
          // 行われることを保証するため、applyQueryFilterによるDOM変更完了を待ちます。
          await this.$nextTick()

          this.$emit('update:activeTab', 'result')

          // 分析は既に完了している場合もありますが、その場合は完了通知を監視に接続した
          // 直後に分析完了通知が飛びます。分析のステータスは気にせず完了待機に入ります。
          this.$queryNotification.connect(queryId)
        } else if (referenceId) {
          // 2. 参照分析IDが提供されている
          const queryFilter = await this.$longQuery.fetchQueryFilter(
            referenceId,
          )
          const override = JSON.parse(this.$route.query.override)

          const mergedQueryFilter = mergeWith(queryFilter, override, (o, s) => {
            return isArray(s) ? s : undefined
          })

          const promises = this.applyQueryFilter(mergedQueryFilter)
          if (promises && promises.length > 0) {
            await Promise.all(promises.filter(p => p))
          }

          if (override.immediate) {
            this.executeQuery()
          }
        } else if (reservationId) {
          // 予約復元は `$route.query.reservation` watcher 側に寄せる
          return
        } else {
          // 3. 分析IDも参照分析IDもない
          this.queryId = undefined
          this.$emit('update:activeTab', 'filter')

          await this.$nextTick()

          // 保存されている最後の分析条件（画面毎）があれば復元する
          const queryFilter = await this.$longQuery.fetchMenuQueryFilter(
            this.resourceName,
          )
          if (queryFilter) {
            try {
              const promises = this.applyQueryFilter(queryFilter)
              if (promises && promises.length > 0) {
                await Promise.all(promises.filter(p => p))
              }
            } catch (error) {
              // バージョンアップなどで内部データ構造が変わると分析条件の復元が
              // できなくなる可能性がある。ひとまず対策として当該データを削除する。
              this.showFailedToApplyQueryFilter = true
              await this.$longQuery.deleteMenuQueryFilter(this.resourceName)
            }
          }
        }
      },
    },
    /**
     * 予約IDの変化
     */
    '$route.query.reservation': {
      // 画面表示時にもキャッチできるようにimmediateとしています
      immediate: true,
      async handler(reservationId) {
        // 予約IDがないとき、または queryId/reference ルートでは処理しない
        if (
          !reservationId ||
          this.$route.params.queryId ||
          this.$route.query.reference ||
          this.disableReservation
        ) {
          return
        }
        // 予約条件を復元している最中は復元中フラグを立てる
        this.$store.commit(SET_QUERY_FILTER_RESTORING, true)

        // reservationIdをもとに、予約の詳細情報を取得しに行く
        const reservationConfig = await this.$repositories
          .get('queryReservation')
          .getReservationConfig(reservationId)
        // 予約情報で、編集不可のステータスの場合は処理中のため編集できない
        if (
          !(
            canEditReservation(reservationConfig.status) ||
            canResubmitReservation(reservationConfig.status)
          )
        ) {
          this.$store.commit(SET_QUERY_FILTER_RESTORING, false)
          this.$dialog.open(
            DialogWarning,
            'この予約は処理中のため編集できません。分析予約一覧画面に移動します。',
          )
          this.$router.replace(`/query-management`)
          return
        }

        this.applyReservationConfig(reservationConfig, reservationId)

        // QueryFilterの復元
        const queryFilter = await this.$repositories
          .get('queryReservation')
          .getQueryFilter(reservationId)
        // 分析条件の復元を行う
        const promises = this.applyQueryFilter(queryFilter)
        if (promises && promises.length > 0) {
          await Promise.all(promises.filter(p => p))
        }

        // QueryFilterへの反映が完了し、各条件コンポーネントのエラー判定が
        // 行われることを保証するため、applyQueryFilterによるDOM変更完了を待ちます。
        await this.$nextTick()
        // 復元中フラグを落とす
        this.$store.commit(SET_QUERY_FILTER_RESTORING, false)
        // 分析条件タブを表示
        this.$emit('update:activeTab', 'filter')
      },
    },
  },
  methods: {
    async executeQueryFilterSaveWrapper() {
      let queryFilterSaved
      this.waitingTabSwitch = true
      if (this.convertMenuDefaultQueryFilter) {
        const qfClone = JSON.parse(JSON.stringify(this.queryFilter))
        queryFilterSaved = this.convertMenuDefaultQueryFilter(qfClone)
      } else {
        queryFilterSaved = this.queryFilter
      }

      await this.$longQuery.saveMenuQueryFilter(
        this.resourceName,
        queryFilterSaved,
      )
      await this.executeQuery()
    },
    async executeQuery() {
      this.$emit('update:customers', [])

      // 顧客ファイルの指定がある場合、そのままクエリAPIには送信できないので、
      // 一旦アップロードして一時顧客グループIDを取得してから分析に進む。
      if (
        this.queryFilter.customer &&
        !this.queryFilter.customer.customerGroupId &&
        this.queryFilter.customer.file
      ) {
        const customerGroup = await this.uploadCustomerFile(
          this.queryFilter.customer.file,
        )

        if (customerGroup.invalidFile) {
          this.hasCustomerFileUploadWarning = true
          this.customerFileUploadWarningMessage =
            '顧客一覧ファイルのファイルフォーマットが不正です。'
          return
        }

        if (customerGroup.numRows === 0) {
          this.hasCustomerFileUploadWarning = true
          this.customerFileUploadWarningMessage =
            '顧客一覧ファイルに有効な顧客が存在しませんでした。'
          return
        }

        this.queryFilter.customer.customerGroupId =
          customerGroup.customerGroupId
      }

      this.querying = true

      let payload = this.mapQueryFilterForApi(this.queryFilter)

      try {
        // Promiseの完了を待機する。オブジェクトが返ってきた場合、payloadには
        // 値がそのまま格納され後続の処理が行われる。
        payload = await Promise.resolve(payload)
      } catch (error) {
        if (error.name === 'AppError') {
          // 論理エラーは分析処理を行わないだけ
          this.$dialog.open(DialogWarning, error.message)
          return
        } else {
          throw error
        }
      }

      const queryInfo = await this.$longQuery.executeQuery(
        this.resourceName,
        payload,
      )

      // この時点で分析画面（リソース）と条件（画面によっては顧客セグメントがあったり）が
      // 揃っているので、この分析から "遷移できない" ルートの登録を行う。
      // ルートの登録には、前回分析までで潰れたルートに追加するので、referenceでもらった
      // 前回の分析IDも必要です。
      await this.$longQuery.pushBlockRoute(
        {
          ref: this.$route.query.reference,
          prev: this.queryId,
          current: queryInfo.id,
        },
        this.resourceName,
        this.queryFilter.option && this.queryFilter.option.customerSegment,
      )
      await this.$longQuery.saveQueryFilter(queryInfo.id, this.queryFilter)

      // 分析IDを取得したので、IDを含む分析結果表示画面に遷移します。
      const nextPage = `/query-${this.resourceName}/${queryInfo.id}`
      if (this.$route.query.reference) {
        this.$router.replace(nextPage)
      } else {
        this.$router.push(nextPage)
      }

      this.waitingTabSwitch = false
    },
    /**
     * 予約登録モードでの分析開始処理
     */
    async reserveQuery() {
      if (this.disableReservation) {
        return
      }
      this.waitingTabSwitch = true
      // 予約直前も予約数のチェックを行う(新規の場合のみ)
      if (!this.reservationConfig.isEdit) {
        const reservationQuota = await this.$repositories
          .get('queryReservation')
          .getReservationQuota()
        if (
          reservationQuota.reservationActiveCount >=
          reservationQuota.reservationLimit
        ) {
          this.$dialog.open(
            DialogWarning,
            '予約数が上限に達しているため、新規の予約はできません。',
          )
          this.waitingTabSwitch = false
          return
        }
      }
      // 予約ステータスの確認
      if (this.reservationId) {
        const reservationConfig = await this.$repositories
          .get('queryReservation')
          .getReservationConfig(this.reservationId)

        // 最新ステータスが変わっていて、RequiresUpdate以外の場合はエラー
        if (
          reservationConfig.status !== 'RequiresUpdate' &&
          reservationConfig.status !== this.reservationConfig.status
        ) {
          this.$dialog.open(
            DialogWarning,
            '予約ステータスが最新の情報と異なります。予約一覧画面で確認してください。',
          )
          this.waitingTabSwitch = false
          return
        }
      }

      // 顧客ファイルの指定がある場合、そのままクエリAPIには送信できないので、
      // 一旦アップロードして一時顧客グループIDを取得してから分析に進む。
      this.$emit('update:customers', [])
      if (
        this.queryFilter.customer &&
        !this.queryFilter.customer.customerGroupId &&
        this.queryFilter.customer.file
      ) {
        const customerGroup = await this.uploadCustomerFile(
          this.queryFilter.customer.file,
        )

        if (customerGroup.invalidFile) {
          this.hasCustomerFileUploadWarning = true
          this.customerFileUploadWarningMessage =
            '顧客一覧ファイルのファイルフォーマットが不正です。'
          return
        }

        if (customerGroup.numRows === 0) {
          this.hasCustomerFileUploadWarning = true
          this.customerFileUploadWarningMessage =
            '顧客一覧ファイルに有効な顧客が存在しませんでした。'
          return
        }

        this.queryFilter.customer.customerGroupId =
          customerGroup.customerGroupId
      }

      this.querying = true

      let payload = this.mapQueryFilterForApi(this.queryFilter)

      try {
        // Promiseの完了を待機する。オブジェクトが返ってきた場合、payloadには
        // 値がそのまま格納され後続の処理が行われる。
        payload = await Promise.resolve(payload)
      } catch (error) {
        if (error.name === 'AppError') {
          // 論理エラーは分析処理を行わないだけ
          this.$dialog.open(DialogWarning, error.message)
          return
        } else {
          throw error
        }
      }
      const reservePayload = {
        reservationId: this.reservationId,
        queryFilter: payload,
        displayQueryFilter: this.queryFilter,
        reservationConfig: this.reservationConfig,
      }

      let reservationId
      try {
        reservationId = await this.$repositories
          .get('queryReservation')
          .reserveQuery(this.resourceName, reservePayload)
      } catch (error) {
        this.querying = false
        this.waitingTabSwitch = false

        // API側で組立されたメッセージを表示
        if (
          error &&
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          this.$dialog.open(DialogWarning, {
            message: error.response.data.message,
            multiline: true,
          })
        } else {
          // 予期しないエラーは再throw
          throw error
        }
        return
      }

      // 予約実行後、元の予約がNextReservationFailedの場合はそれをCompletedに更新する。
      if (this.isNextReservationFailed && this.parentReservationId) {
        await this.$repositories
          .get('queryReservation')
          .closeReservation(this.parentReservationId)
      }

      // 最新の予約設定を取得して反映
      const latestReservationConfig = await this.$repositories
        .get('queryReservation')
        .getReservationConfig(reservationId)

      this.applyReservationConfig(latestReservationConfig, reservationId)

      // 予約IDを取得したので、予約IDを含む画面に遷移
      // 連続で登録は不可とする
      this.querying = false
      this.snackbar.message = '分析予約が完了しました。'
      this.snackbar.show = true
      const nextPage = {
        path: `/query-${this.resourceName}`,
        query: {
          reservation: reservationId,
          reservationUpdatedAt: Date.now().toString(),
        },
      }
      if (this.$route.query.reservation) {
        this.$router.replace(nextPage)
      } else {
        this.$router.push(nextPage)
      }

      this.waitingTabSwitch = false
    },
    /**
     * 顧客一覧ファイルをアップロードして一時顧客グループを作成する
     */
    async uploadCustomerFile(file) {
      this.uploadingCustomerFile = true
      const customerGroup = await this.$customerGroup.upload(file)
      this.uploadingCustomerFile = false

      return customerGroup
    },
    /**
     * クエリの進捗通知（完了）受信時処理
     */
    async loadQueryResult({ id: queryId, status }) {
      if (this.queryId !== queryId) {
        return
      }

      this.querying = false

      if (status === 'Error') {
        this.$emit('update:activeTab', 'filter')
        throw new Error('分析時にエラーが発生しました。')
      }

      const queryInfo = await this.$longQuery.fetchQueryInfo(
        this.resourceName,
        queryId,
      )

      const url = await this.$longQuery.getQueryResultFetchUrl(
        this.resourceName,
        queryId,
      )

      if (this.onQueryCompleted) {
        const queryFilter = await this.$longQuery.fetchQueryFilter(queryId)

        const { stop } =
          (await this.onQueryCompleted({ queryFilter, queryInfo })) || {}
        if (stop) {
          this.$emit('update:activeTab', 'filter')
          return
        }
      }

      const isQueryFullFeatured =
        queryInfo.queryFullFeatured !== undefined &&
        queryInfo.queryFullFeatured !== undefined
          ? queryInfo.queryFullFeatured
          : true
      this.$store.commit(SET_IS_QUERY_FULL_FEATURED, isQueryFullFeatured)
      // 制限モードの場合、分析条件で復元不可のものが無いかチェック
      if (!queryInfo.queryFullFeatured) {
        initializeRestrictedQueryFilter(this.queryFilter)
      }

      if (!queryInfo.hasData) {
        this.showNoResultDialog = true
        this.$emit('update:activeTab', 'filter')
        return
      }

      // 分析IDが含まれるURLを開く時点で分析結果タブがフォーカスされているはずですが、
      // 分析中に分析条件タブに移動しているような場合でも強制的に分析結果タブを開きます。
      this.$emit('update:activeTab', 'result')

      // 他画面への遷移が可能かどうかによってリンクの色を変える
      if (!this.$store.state.isQueryFullFeatured) {
        document.documentElement.style.setProperty('--link-color', '#999')
      } else {
        document.documentElement.style.setProperty('--link-color', '#448aff')
      }

      await this.$nextTick()

      const elem = this.$refs.resultContainer
      this.$emit('result-area-size-change', {
        width: elem.clientWidth - 2,
        height: elem.clientHeight,
      })

      this.$emit('update:groupHeaders', queryInfo.grid.groupHeader)
      this.$emit('update:columns', queryInfo.grid.colModel)
      this.$emit('update:resultFetchUrl', url)

      this.$emit('query-completed', queryInfo)
    },
    /**
     * 顧客コレクションへの登録
     */
    addCustomerCollection(customer) {
      const cells = document.querySelectorAll(
        `[data-customer-collection="${customer.id}"]`,
      )

      for (const cell of cells) {
        cell.classList.toggle('customer-collection-selected')
      }

      this.$emit('update:customers', [customer, ...this.customers])
    },
    /**
     * 顧客コレクションからの削除
     */
    removeCustomerCollection(customer) {
      const cells = document.querySelectorAll(
        `[data-customer-collection="${customer.id}"]`,
      )

      for (const cell of cells) {
        cell.classList.toggle('customer-collection-selected')
      }

      this.$emit(
        'update:customers',
        this.customers.filter(c => c.id !== customer.id),
      )
    },
    /**
     * 顧客コレクションに存在すれば削除、存在しなければ追加
     */
    toggleCustomerCollection(customer) {
      if (this.customers.find(c => c.id === customer.id)) {
        this.removeCustomerCollection(customer)
      } else {
        this.addCustomerCollection(customer)
      }
    },
    /**
     * 顧客コレクション内容変更
     */
    updateCustomers(customers) {
      // 顧客コレクションでの選択を反映させる。
      // 現状顧客コレクション上では削除しかできないので、現顧客リストにあって
      // 新顧客リストにない顧客のセルの選択済みCSSクラスを削除する対応のみ
      differenceBy(this.customers, customers, 'id').forEach(customer => {
        const cells = document.querySelectorAll(
          `[data-customer-collection="${customer.id}"]`,
        )
        for (const cell of cells) {
          cell.classList.remove('customer-collection-selected')
        }
      })

      // ※ これは全セル走査して同期させる実装（顧客コレクション上の操作が削除だけでは
      //    なくなったらこんなのも必要になるかも。パフォーマンスは考慮していない、、、）
      // const cells = document.querySelectorAll(`[data-customer-collection]`)
      // const ids = new Set(customers.map(c => c.id))
      // for (const cell of cells) {
      //   if (ids.has(cell.getAttribute('data-customer-collection'))) {
      //     cell.classList.add('customer-collection-selected')
      //   } else {
      //     cell.classList.remove('customer-collection-selected')
      //   }
      // }

      this.$emit('update:customers', customers)
    },
    /**
     * 顧客一覧のダウンロード
     *
     * 顧客のダウンロードは画面に関係なく顧客コレクションに登録されている要素から
     * 実施可能なのでここで行う。
     */
    async downloadCustomers(customers) {
      if (!this.queryId) {
        // 分析結果画面に配置している顧客コレクションからのイベントなので、
        // 分析IDは必ずあるはずなので通常ここは通らない。
        throw new Error('分析IDがありません')
      }
      if (!customers || customers.length === 0) {
        // 顧客が選択されていない状態では顧客コレクションは開いていないので、
        // 通常ここは通らない。
        throw new Error('顧客が選択されていません')
      }

      this.downloadingCustomerFile = true

      try {
        const customerGroup = await this.$customerGroup.create(
          this.queryId,
          customers.map(c => c.id),
        )
        const url = await this.$customerGroup.archive(
          customerGroup.customerGroupId,
        )

        window.open(url)
      } catch (e) {
        this.downloadingCustomerFile = false
        throw e
      }

      this.downloadingCustomerFile = false
    },
    /**
     * Excelファイルをダウンロードする
     *
     * propsであるexcelPropertiesHandlerを呼び出し、Excel作成URLのクエリパラメータとします。
     */
    async downloadExcel() {
      this.downloadingExcel = true

      try {
        const extraProps = this.excelPropertiesHandler()

        if (extraProps === false) {
          return
        }

        const url = await this.$longQuery.getExcelUrl(
          this.resourceName,
          this.queryId,
          extraProps,
        )

        for (const u of Array.isArray(url) ? url : [url]) {
          window.open(u)
        }
      } finally {
        this.downloadingExcel = false
      }
    },
    async transition(t) {
      // 画面遷移時には顧客セル選択（->一時顧客グループ作成）が必須
      this.creatingCustomerGroup = true

      let customerGroup
      try {
        customerGroup = await this.$customerGroup.create(
          this.queryId,
          this.customers.map(c => c.id),
        )
        this.creatingCustomerGroup = false
      } catch (error) {
        this.creatingCustomerGroup = false
        throw error
      }

      const nextPage = t.transition({
        destination: t.menuCode,
        queryId: this.queryId,
        override: {
          customer: {
            customerGroupId: customerGroup.customerGroupId,
          },
          immediate: this.immediate,
        },
      })

      window.open(`${process.env.BASE_URL}${nextPage}`)
    },
    openHelp() {
      window.open(
        `${process.env.VUE_APP_HELP_SITE_URL_BASE}${this.menu.menuCode}.html`,
      )
    },
    async handleDumpUploaded({ queryFilter, menuCode }) {
      if (this.menu.menuCode !== menuCode) {
        this.$dialog.open(
          DialogWarning,
          'この分析条件はこの画面のものではありません。',
        )
        return
      }

      try {
        const promises = this.applyQueryFilter(queryFilter)
        if (promises && promises.length > 0) {
          await Promise.all(promises.filter(p => p))
        }

        this.snackbar.message = '分析条件を復元しました'
        this.snackbar.show = true
      } catch (error) {
        // ダンプからの分析条件復元に失敗した場合はユーザーに通知する
        // （データ構造変更や壊れたファイルなどが原因の可能性あり）
        // 既存の「保存済み分析条件の復元」と同様に警告を表示する。
        // eslint-disable-next-line no-console
        console.error('Failed to restore analysis conditions from dump.', error)
        this.$dialog.open(
          DialogWarning,
          '分析条件の復元に失敗しました。保存された内容が古いか、ファイルが破損している可能性があります。',
        )
      }
    },
    applyReservationConfig(reservationConfig, reservationId) {
      this.isNextReservationFailed = false
      this.parentReservationId = undefined
      // 予約復元時の処理（reservationIdが渡された場合）
      if (reservationId !== undefined) {
        // 元の config を保存（将来の変更点洗い出し用）
        reservationConfig.originalConfig = JSON.parse(
          JSON.stringify(reservationConfig),
        )

        // NextReservationFailedの場合は、新規作成としてreservationGroupIdを保持
        this.isNextReservationFailed =
          reservationConfig.status === 'NextReservationFailed'
        const isEdit = canEditReservation(reservationConfig.status)
        this.parentReservationId = reservationId
        this.reservationId = isEdit ? reservationId : undefined
        reservationConfig.mode = 'reserve'

        // RequiresUpdate → Waiting に変換
        if (reservationConfig.status === 'RequiresUpdate') {
          reservationConfig.status = 'Waiting'
        }

        // 予約情報のステータスがErrorまたはCompletedの場合は新規登録にする（reservationGroupIdは引き継がない）
        // NextReservationFailedの場合は新規登録にするが、reservationGroupIdは引き継ぐ
        reservationConfig.isEdit = isEdit
        if (!isEdit && !this.isNextReservationFailed) {
          // NextReservationFailed以外の場合は、reservationGroupIdもクリア
          reservationConfig.reservationGroupId = undefined
        }
      }
      this.reservationConfig = {
        ...this.reservationConfig,
        ...reservationConfig,
      }
    },
  },
  async created() {
    // ちょっとキモいが、EventBusを用意するほどでもないので、このレイアウトを使用
    // しているコンポーネントで'toggle-customer-collection'が発生したらこの
    // コンポーネントが顧客リストを操作する。
    this.$parent.$on(
      'toggle-customer-collection',
      this.toggleCustomerCollection,
    )
    // クエリ通知
    const subscription = this.$queryNotification.observable.subscribe(
      this.loadQueryResult,
    )
    this.$on('hook:beforeDestroy', () => subscription.unsubscribe())
  },
  mounted() {
    // refで指定したhtml要素はmountedにならないと見つからないのでCreatedでは以下の
    // 処理はできません。
    this.$windowResize.subscribe(() => {
      const elem = this.$refs.resultContainer
      if (elem) {
        this.$emit('result-area-size-change', {
          width: elem.clientWidth - 2,
          height: elem.clientHeight,
        })
      }
    })
  },
}
</script>

<style lang="scss" scoped>
$header-height: 72px;
$filter-summary-width: 320px;

main {
  display: flex;
  height: 100vh;
  position: relative;
}

.header {
  background: #fff;
  height: $header-height;
  top: 0;
  left: 0;
  right: 0;
  padding-right: var(--spacing-large);
  opacity: 0.9;
  position: fixed;
  z-index: 11;

  display: flex;
  align-items: center;

  .logo {
    flex: 0 0 $filter-summary-width;
    height: $header-height;
    background: var(--bg-color-accent);
  }

  .align-left {
    flex: 1;
    margin-left: var(--spacing-large);
  }
}

.header-actions {
  display: flex;
  align-items: center;
}

.filter-summary-wrapper {
  position: fixed;
  left: 0;
  top: 0;
  overflow: hidden;
  height: 100%;
  width: $filter-summary-width;
  z-index: 10;
}

.filter-summary {
  padding-top: $header-height;
  height: 100%;
  background: var(--bg-color-accent);
  overflow-x: hidden;
  overflow-y: auto;
}

.filter-summary-body {
  flex: 1;
  background: transparent;
}

.contents {
  position: relative;
  padding-top: $header-height;
  padding-bottom: var(--spacing-large);
  padding-left: var(--spacing-large);
  padding-right: var(--spacing-large);
  margin-top: var(--spacing-large);
  margin-left: $filter-summary-width;
  width: 100%;
}

.filter-querying-overlay {
  position: absolute;
  inset: 0;
  z-index: 12;
  background: rgba(255, 255, 255, 0.65);
}

.filter {
  // 分析条件内に描画される分析条件パネルの表示位置はここで制御します。
  .query-filter-element {
    margin-top: var(--spacing-large);
    &:first-child {
      margin-top: 0;
    }
    &:last-child {
      margin-bottom: var(--spacing-large);
    }
  }
}

.fixed-action-area {
  position: fixed;
  bottom: var(--spacing-medium);
  right: var(--spacing-medium);
  z-index: 11;
}

.result-container {
  height: 100%;
  position: relative;
  width: 100%;
}

.spinner-container {
  position: relative;
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;

  img {
    position: absolute;
    width: 200px;
    opacity: 0.4;
    transition: opacity 0.8s ease-out, transform 0.8s ease-out;
    &:hover {
      opacity: 0.8;
      transform: scale(1.1);
    }
  }
}

.slide-in-enter-active,
.slide-in-leave-active {
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
}
.slide-in-enter,
.slide-in-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.query-button-enter-active,
.query-button-leave-active {
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
}
.query-button-enter,
.query-button-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.avatar {
  margin-left: var(--spacing-small);
  img {
    object-fit: cover;
  }
}
</style>

<style lang="scss">
.c3t-cell {
  &.customer-collection-cell,
  &.link-cell {
    cursor: pointer;
    color: var(--link-color, #448aff) !important;
    text-decoration: underline;
  }
  &.customer-collection-selected {
    color: inherit;
    background: #bdbdbd !important;
    text-decoration: none;
  }
}

// C3-Tableのスタイル暫定上書き
.c3t-theme-default {
  .c3t-container {
    background-color: #fff !important;
  }
}

.spin {
  animation: spin 0.4s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

// Google検索可能な商品名のリンク化
.google-search-cell {
  cursor: pointer;
  // colorは、新商品の場合、cell.style.color が指定されているため、そちらを優先するために !important は使用しない。
  // インラインstyleをCSSクラスより優先させるため、ここでは!importantを使用しない。
  color: var(--md-theme-default-primary-on-background, #448aff);
  text-decoration: underline;
}
</style>
